import { useSceneScale } from "@/landing/use-scene-scale.ts";

const BASE = import.meta.env.BASE_URL;
const SCENE_W = 1440;
const SCENE_H = 800;

const GATES = [
  {
    tone: "blue",
    n: "1",
    title: ["数据不能离开，", "模型进不来"],
    body: "云端模型越便捷，隐私、合规与数据主权的边界越清晰。核心数据不能出域，AI 也就停在外围。"
  },
  {
    tone: "violet",
    n: "2",
    title: ["数据没有上下文，", "智能无法积累"],
    body: "信息散落在系统、文档和人的经验里。没有统一语境与持续记忆，每个 AI 应用都从零开始。"
  },
  {
    tone: "amber",
    n: "3",
    title: ["系统不能停，", "业务换不起芯"],
    body: "流程、权限与旧系统彼此咬合。通用产品无法贴进复杂业务，真正落地必须在企业内部深度适配。"
  }
] as const;

const FILE_TAGS = [
  { label: "文档", x: 18, y: 8 },
  { label: "邮件", x: 14, y: 64 },
  { label: "系统记录", x: 38, y: 4 },
  { label: "Excel 表格", x: 36, y: 46 },
  { label: "知识碎片", x: 58, y: 62 },
  { label: "经验", x: 22, y: 78 },
  { label: "聊天记录", x: 48, y: 82 }
] as const;

const SYSTEM_TAGS = [
  { label: "审批流程", x: 18, y: 8 },
  { label: "权限体系", x: 62, y: 4 },
  { label: "核心业务系统", x: 48, y: 38 },
  { label: "数据库", x: 78, y: 22 },
  { label: "报表系统", x: 42, y: 72 },
  { label: "历史系统", x: 70, y: 78 },
  { label: "…", x: 90, y: 58 }
] as const;

const X_TICKS = ["00", "10", "20", "30", "40", "50", "60", "70", "80", "90", "100"];
const Y_TICKS = ["A", "B", "C", "D"];

const WIRES = {
  pipe: "M 236 444 C 262 444 279 515 301 407 C 310 374 323 367 341 369",
  feed: "M 332 420 C 350 422 370 430 396 432",
  bridgeHigh: "M 785 396 C 840 382 890 370 942 374",
  bridgeLow: "M 785 448 C 840 440 905 448 958 442"
} as const;

function plate(name: string) {
  return `${BASE}why/${name}.png`;
}

function GateCopy({ gate }: { gate: (typeof GATES)[number] }) {
  return (
    <>
      <div className="why-gate-title">
        <span className="why-num" aria-hidden>
          {gate.n}
        </span>
        <strong>
          {gate.title[0]}
          <br />
          {gate.title[1]}
        </strong>
      </div>
      <p>{gate.body}</p>
    </>
  );
}

function WhyScene() {
  const { frameRef, scale } = useSceneScale(SCENE_W);

  return (
    <div className="why-scene-frame" ref={frameRef} style={{ height: SCENE_H * scale }}>
      <div className="why-scene" style={{ transform: `scale(${scale})` }}>
        <div className="why-floor" aria-hidden>
          <div className="why-floor-grid" />
        </div>

        <svg className="why-grid-svg" viewBox={`0 0 ${SCENE_W} ${SCENE_H}`} aria-hidden>
          {Y_TICKS.map((tick, i) => (
            <text key={tick} className="why-axis-label" x="14" y={200 + i * 140}>
              {tick}
            </text>
          ))}
          {X_TICKS.map((tick, i) => (
            <text key={tick} className="why-axis-label" x={40 + i * 136} y="786">
              {tick}
            </text>
          ))}
        </svg>

        <svg className="why-wires" viewBox={`0 0 ${SCENE_W} ${SCENE_H}`} aria-hidden>
          <defs>
            <linearGradient id="why-pipe" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#3d8bff" />
              <stop offset="1" stopColor="#8ec2ff" />
            </linearGradient>
            <filter id="why-pipe-glow" x="-40%" y="-80%" width="180%" height="260%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            className="why-pipe-bloom"
            d={WIRES.pipe}
            fill="none"
            stroke="url(#why-pipe)"
            strokeWidth="18"
            strokeLinecap="round"
            filter="url(#why-pipe-glow)"
          />
          <path
            className="why-pipe-core"
            d={WIRES.pipe}
            fill="none"
            stroke="#d7e8ff"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <path
            className="why-pipe-pulse"
            d={WIRES.pipe}
            fill="none"
            stroke="#ffffff"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            className="why-feed why-wire-base"
            d={WIRES.feed}
            fill="none"
            strokeWidth="1.6"
            strokeDasharray="3 6"
          />
          <path
            className="why-feed why-wire-pulse"
            d={WIRES.feed}
            fill="none"
            strokeWidth="2.2"
          />
          <path
            className="why-bridge why-wire-base"
            d={WIRES.bridgeHigh}
            fill="none"
            strokeWidth="1.6"
            strokeDasharray="3 6"
          />
          <path
            className="why-bridge why-wire-pulse"
            d={WIRES.bridgeHigh}
            fill="none"
            strokeWidth="2.2"
          />
          <path
            className="why-bridge why-wire-base"
            d={WIRES.bridgeLow}
            fill="none"
            strokeWidth="1.4"
            strokeDasharray="3 7"
          />
          <path
            className="why-bridge why-wire-pulse"
            d={WIRES.bridgeLow}
            fill="none"
            strokeWidth="2"
          />
          <path
            className="why-drop"
            data-tone="blue"
            d="M 377 170 C 370 196 356 214 342 232"
            fill="none"
            strokeWidth="1.2"
            strokeDasharray="2 5"
          />
          <path
            className="why-drop"
            data-tone="violet"
            d="M 797 170 C 800 190 806 206 814 222"
            fill="none"
            strokeWidth="1.2"
            strokeDasharray="2 5"
          />
          <path
            className="why-drop"
            data-tone="amber"
            d="M 1085 170 C 1090 216 1100 260 1112 300"
            fill="none"
            strokeWidth="1.2"
            strokeDasharray="2 5"
          />
        </svg>

        <figure className="why-asset" data-id="cloud" style={{ left: 18, top: 356, width: 232 }}>
          <img src={plate("cloud")} alt="" />
          <figcaption>
            <strong>云端能力</strong>
            <span>通用模型与服务</span>
          </figcaption>
        </figure>

        <figure className="why-asset" data-id="wall" style={{ left: 236, top: 145, height: 592 }}>
          <img src={plate("wall")} alt="" />
          <figcaption className="why-boundary">
            <i />
            企业边界
          </figcaption>
          <div className="why-forbid">
            数据出域
            <span>不可行</span>
          </div>
        </figure>

        <figure className="why-asset" data-id="files" style={{ left: 365, top: 320, width: 438 }}>
          <img src={plate("files")} alt="" />
          {FILE_TAGS.map((tag) => (
            <span key={tag.label} className="why-tag" style={{ left: `${tag.x}%`, top: `${tag.y}%` }}>
              {tag.label}
            </span>
          ))}
        </figure>

        <figure className="why-asset" data-id="plane" style={{ left: 620, top: 132, height: 620 }}>
          <img src={plate("plane")} alt="" />
        </figure>

        <figure className="why-asset" data-id="systems" style={{ left: 750, top: 288, width: 690 }}>
          <img src={plate("systems")} alt="" />
          {SYSTEM_TAGS.map((tag) => (
            <span key={tag.label} className="why-tag" data-tone="amber" style={{ left: `${tag.x}%`, top: `${tag.y}%` }}>
              {tag.label}
            </span>
          ))}
        </figure>

        {GATES.map((gate, index) => (
          <article
            key={gate.n}
            className="why-gate"
            data-tone={gate.tone}
            style={{ left: [360, 780, 1068][index], top: 20, width: [300, 270, 348][index] }}
          >
            <GateCopy gate={gate} />
          </article>
        ))}
      </div>
    </div>
  );
}

export function WhyGates() {
  return (
    <section className="why-gates" id="why">
      <header className="why-gates-head">
        <h2>
          AI 已经通向了未来，
          <br />
          但大多数企业还被困在昨天。
        </h2>
        <p>
          真正阻挡智能进入核心业务的，不是模型能力，而是三道同时存在的结构性门槛。
        </p>
      </header>

      <WhyScene />

      <div className="why-gates-mobile">
        {GATES.map((gate, index) => (
          <article key={gate.n} className="why-gate" data-tone={gate.tone}>
            <div className="why-beat-visual" aria-hidden>
              {index === 0 ? (
                <>
                  <img src={plate("cloud")} alt="" />
                  <img src={plate("wall")} alt="" />
                </>
              ) : (
                <img src={plate(index === 1 ? "files" : "systems")} alt="" />
              )}
            </div>
            <GateCopy gate={gate} />
          </article>
        ))}
      </div>

      <p className="why-gates-close">
        这不是一个工具缺口，而是一层新的企业智能基础设施。
      </p>
      <div className="why-gates-transition" aria-hidden>
        <span className="why-chevron" />
      </div>
    </section>
  );
}
